import {
  applyCompletedJobToDeck,
  isActiveJobStatus,
  listQueuedJobs,
  loadJobQueue,
  markJobApplied,
  queuedJobFromServerJob,
  queuedJobFromSubmit,
  removeQueuedJob,
  resolveJobOutput,
  saveJobQueue,
  touchContentUpdatedAt,
  updateQueuedJob,
  type SurfDeck,
  type SurfJobSubmitResult,
  type SurfQueuedJob,
  SurfCloudAPI,
} from "@vida/surf-core";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { currentUserSub } from "../auth";
import { createSurfApiConfig, ensureSurfClientConfig } from "../services/surfClientConfig";
import { useSurfLibrary } from "./useSurfLibrary";

const POLL_MS = 1500;
const MAX_CONCURRENT_POLLS = 4;

type DeckCommitFn = (deck: SurfDeck) => void;

type SurfJobQueueContextValue = {
  jobs: SurfQueuedJob[];
  deckJobs: (deckId?: string) => SurfQueuedJob[];
  enqueueSubmit: (
    submit: SurfJobSubmitResult,
    options?: { preparedDeck?: SurfDeck },
  ) => Promise<void>;
  dismissJob: (jobId: string) => Promise<void>;
  retryJob: (jobId: string) => Promise<void>;
  restoreVersion: (job: SurfQueuedJob) => Promise<void>;
  hasActiveJobForTarget: (
    deckId: string,
    slideId?: string,
    layerId?: string,
  ) => boolean;
  registerDeckEditor: (
    deckId: string,
    commit: DeckCommitFn,
    getLiveDeck?: () => SurfDeck | null,
  ) => void;
  unregisterDeckEditor: (deckId: string) => void;
  ready: boolean;
};

const SurfJobQueueContext = createContext<SurfJobQueueContextValue | null>(null);

export function SurfJobQueueProvider({ children }: { children: ReactNode }) {
  const { getDeck, saveDeck } = useSurfLibrary();
  const [jobs, setJobs] = useState<SurfQueuedJob[]>([]);
  const [ready, setReady] = useState(false);
  const jobsRef = useRef(jobs);
  const editorsRef = useRef(new Map<string, DeckCommitFn>());
  const liveDeckGettersRef = useRef(new Map<string, () => SurfDeck | null>());
  // Overlay of the latest applied deck per deckId: chains concurrent job applies
  // so each builds on the previous result (prevents stale-base overwrite → lost widget).
  const pendingDeckRef = useRef(new Map<string, SurfDeck>());
  const pollingRef = useRef(new Set<string>());
  const applyingRef = useRef(new Set<string>());
  const userId = currentUserSub();

  jobsRef.current = jobs;

  const persist = useCallback(
    async (next: SurfQueuedJob[]) => {
      jobsRef.current = next;
      setJobs(next);
      await saveJobQueue(userId, next);
    },
    [userId],
  );

  const applyJobToDeck = useCallback(
    async (job: SurfQueuedJob, output: { kind: "asset"; assetKey: string } | { kind: "text"; text: string }) => {
      const current = jobsRef.current.find((j) => j.jobId === job.jobId);
      if (!current || current.status === "applied") return;
      // Guard against concurrent/duplicate apply of the same job (tick + poll).
      if (applyingRef.current.has(job.jobId)) return;
      applyingRef.current.add(job.jobId);
      try {
        // Build on the latest applied result (pending overlay) > editor live deck > library,
        // computed synchronously so concurrent applies don't read a stale base.
        const liveDeck = liveDeckGettersRef.current.get(job.deckId)?.() ?? null;
        const baseDeck =
          pendingDeckRef.current.get(job.deckId) ?? liveDeck ?? getDeck(job.deckId)?.deck ?? null;
        if (!baseDeck) return;

        const nextDeck = touchContentUpdatedAt(
          applyCompletedJobToDeck(baseDeck, job, output),
        );
        pendingDeckRef.current.set(job.deckId, nextDeck);

        const editorCommit = editorsRef.current.get(job.deckId);
        if (editorCommit) {
          editorCommit(nextDeck);
        } else {
          await saveDeck(nextDeck, job.deckId);
        }

        const withKey = updateQueuedJob(jobsRef.current, job.jobId, {
          ...(output.kind === "asset" ? { outputAssetKey: output.assetKey } : {}),
        });
        await persist(markJobApplied(withKey, job.jobId));
      } finally {
        applyingRef.current.delete(job.jobId);
      }
    },
    [getDeck, persist, saveDeck],
  );

  const pollOne = useCallback(
    async (jobId: string) => {
      if (pollingRef.current.has(jobId)) return;
      pollingRef.current.add(jobId);
      try {
        await ensureSurfClientConfig();
        const cfg = createSurfApiConfig();
        const current = jobsRef.current.find((j) => j.jobId === jobId);
        if (!current || current.status === "applied") return;
        const result = await resolveJobOutput(cfg, jobId, { op: current.op });

        if (result.status === "COMPLETED" && result.output?.kind === "asset") {
          await persist(
            updateQueuedJob(jobsRef.current, jobId, {
              status: "completed",
              outputAssetKey: result.output.assetKey,
            }),
          );
          const updated = jobsRef.current.find((j) => j.jobId === jobId);
          if (updated) {
            await applyJobToDeck(updated, result.output);
          }
          return;
        }

        if (result.status === "COMPLETED" && result.output?.kind === "text") {
          await persist(
            updateQueuedJob(jobsRef.current, jobId, {
              status: "completed",
            }),
          );
          const updated = jobsRef.current.find((j) => j.jobId === jobId);
          if (updated) {
            await applyJobToDeck(updated, result.output);
          }
          return;
        }

        if (
          result.status === "FAILED" ||
          result.status === "TIMED_OUT" ||
          result.status === "CANCELLED"
        ) {
          await persist(
            updateQueuedJob(jobsRef.current, jobId, {
              status: "failed",
              error: result.error ?? `Job ${result.status}`,
            }),
          );
          return;
        }

        await persist(
          updateQueuedJob(jobsRef.current, jobId, {
            status: result.status === "RUNNING" ? "running" : "pending",
          }),
        );
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        await persist(
          updateQueuedJob(jobsRef.current, jobId, {
            status: "failed",
            error: message,
          }),
        );
      } finally {
        pollingRef.current.delete(jobId);
      }
    },
    [applyJobToDeck, persist],
  );

  const reconcileServer = useCallback(async () => {
    try {
      await ensureSurfClientConfig();
      const cfg = createSurfApiConfig();
      const result = await SurfCloudAPI.listAIJobs(cfg, { appId: "surf", limit: 40 });
      const known = new Set(jobsRef.current.map((j) => j.jobId));
      let next = [...jobsRef.current];

      for (const serverJob of result.items) {
        if (known.has(serverJob.jobId)) continue;
        if (serverJob.status !== "COMPLETED" && serverJob.status !== "RUNNING" && serverJob.status !== "PENDING") {
          continue;
        }
        const queued = queuedJobFromServerJob(serverJob);
        if (!queued) continue;
        next = [...next, queued];
        known.add(serverJob.jobId);
      }

      if (next.length !== jobsRef.current.length) {
        await persist(next);
      }
    } catch {
      /* best-effort */
    }
  }, [persist]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const loaded = await loadJobQueue(userId);
      if (cancelled) return;
      jobsRef.current = loaded;
      setJobs(loaded);
      setReady(true);
      await reconcileServer();
      for (const job of loaded) {
        if (job.status === "completed") {
          try {
            await ensureSurfClientConfig();
            const cfg = createSurfApiConfig();
            const result = await resolveJobOutput(cfg, job.jobId, { op: job.op });
            if (result.output?.kind === "asset") {
              await applyJobToDeck(job, result.output);
            } else if (result.output?.kind === "text") {
              await applyJobToDeck(job, result.output);
            }
          } catch {
            /* skip */
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyJobToDeck, reconcileServer, userId]);

  useEffect(() => {
    if (!ready) return;
    const tick = () => {
      const active = jobsRef.current.filter((j) => isActiveJobStatus(j.status));
      const slots = MAX_CONCURRENT_POLLS - pollingRef.current.size;
      for (const job of active.slice(0, Math.max(0, slots))) {
        void pollOne(job.jobId);
      }
      const completed = jobsRef.current.filter((j) => j.status === "completed");
      for (const job of completed) {
        if (applyingRef.current.has(job.jobId)) continue;
        void (async () => {
          try {
            await ensureSurfClientConfig();
            const cfg = createSurfApiConfig();
            const result = await resolveJobOutput(cfg, job.jobId, { op: job.op });
            if (result.output?.kind === "asset") {
              await applyJobToDeck(job, result.output);
            } else if (result.output?.kind === "text") {
              await applyJobToDeck(job, result.output);
            }
          } catch {
            /* skip */
          }
        })();
      }

      // Drop pending-deck overlays once a deck has no more non-applied jobs,
      // so later manual edits start from the live deck (not a stale overlay).
      for (const deckId of pendingDeckRef.current.keys()) {
        const hasOpen = jobsRef.current.some(
          (j) => j.deckId === deckId && j.status !== "applied" && j.status !== "failed",
        );
        if (!hasOpen) pendingDeckRef.current.delete(deckId);
      }
    };
    tick();
    const id = window.setInterval(tick, POLL_MS);
    return () => window.clearInterval(id);
  }, [applyJobToDeck, pollOne, ready]);

  const enqueueSubmit = useCallback(
    async (submit: SurfJobSubmitResult, options?: { preparedDeck?: SurfDeck }) => {
      if (options?.preparedDeck) {
        const commit = editorsRef.current.get(submit.deckId);
        const touched = touchContentUpdatedAt(options.preparedDeck);
        if (commit) {
          commit(touched);
        } else {
          await saveDeck(touched, submit.deckId);
        }
      }
      const queued = queuedJobFromSubmit(submit);
      const withoutDup = removeQueuedJob(jobsRef.current, submit.jobId);
      await persist([...withoutDup, queued]);
    },
    [persist, saveDeck],
  );

  const dismissJob = useCallback(
    async (jobId: string) => {
      await persist(removeQueuedJob(jobsRef.current, jobId));
    },
    [persist],
  );

  const retryJob = useCallback(
    async (jobId: string) => {
      const job = jobsRef.current.find((j) => j.jobId === jobId);
      if (!job) return;
      await persist(
        updateQueuedJob(jobsRef.current, jobId, {
          status: "pending",
          error: undefined,
          appliedAt: undefined,
        }),
      );
    },
    [persist],
  );

  const restoreVersion = useCallback(
    async (job: SurfQueuedJob) => {
      if (!job.outputAssetKey) return;
      const live = liveDeckGettersRef.current.get(job.deckId)?.() ?? getDeck(job.deckId)?.deck ?? null;
      if (!live) return;
      const next = touchContentUpdatedAt(
        applyCompletedJobToDeck(live, job, { kind: "asset", assetKey: job.outputAssetKey }),
      );
      const editorCommit = editorsRef.current.get(job.deckId);
      if (editorCommit) editorCommit(next);
      else await saveDeck(next, job.deckId);
    },
    [getDeck, saveDeck],
  );

  const deckJobs = useCallback(
    (deckId?: string) => listQueuedJobs(jobs, deckId),
    [jobs],
  );

  const hasActiveJobForTarget = useCallback(
    (deckId: string, slideId?: string, layerId?: string) =>
      jobs.some(
        (job) =>
          job.deckId === deckId &&
          isActiveJobStatus(job.status) &&
          (!slideId || job.target.slideId === slideId) &&
          (!layerId || job.target.layerId === layerId),
      ),
    [jobs],
  );

  const registerDeckEditor = useCallback(
    (deckId: string, commit: DeckCommitFn, getLiveDeck?: () => SurfDeck | null) => {
      editorsRef.current.set(deckId, commit);
      if (getLiveDeck) liveDeckGettersRef.current.set(deckId, getLiveDeck);
    },
    [],
  );

  const unregisterDeckEditor = useCallback((deckId: string) => {
    editorsRef.current.delete(deckId);
    liveDeckGettersRef.current.delete(deckId);
    pendingDeckRef.current.delete(deckId);
  }, []);

  const value = useMemo<SurfJobQueueContextValue>(
    () => ({
      jobs,
      deckJobs,
      enqueueSubmit,
      dismissJob,
      retryJob,
      restoreVersion,
      hasActiveJobForTarget,
      registerDeckEditor,
      unregisterDeckEditor,
      ready,
    }),
    [
      jobs,
      deckJobs,
      enqueueSubmit,
      dismissJob,
      retryJob,
      restoreVersion,
      hasActiveJobForTarget,
      registerDeckEditor,
      unregisterDeckEditor,
      ready,
    ],
  );

  return (
    <SurfJobQueueContext.Provider value={value}>{children}</SurfJobQueueContext.Provider>
  );
}

export function useSurfJobQueue(): SurfJobQueueContextValue {
  const ctx = useContext(SurfJobQueueContext);
  if (!ctx) throw new Error("useSurfJobQueue requires SurfJobQueueProvider");
  return ctx;
}

export function useSurfJobQueueOptional(): SurfJobQueueContextValue | null {
  return useContext(SurfJobQueueContext);
}
